import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { Link } from 'react-router';
import { AUDIT_RETENTION_DAYS } from '@logpulse/types/plans';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

export function CompliancePage() {
  useDocumentMeta({
    title: 'LogPulse Compliance: GDPR, SOC 2, ISO 27001 & DPA',
    description: 'GDPR-compliant log management, SOC 2 Type II in progress, ISO 27001 roadmap, signed DPA, sub-processor list, and audit log retention up to 2 years.',
  });

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'compliance-roadmap', label: 'Compliance Roadmap' },
    { id: 'gdpr', label: 'GDPR', level: 1 },
    { id: 'soc2', label: 'SOC 2 Type II', level: 1 },
    { id: 'iso27001', label: 'ISO 27001', level: 1 },
    { id: 'hipaa', label: 'HIPAA', level: 1 },
    { id: 'dpa', label: 'Data Processing Agreement (DPA)' },
    { id: 'sccs', label: 'Standard Contractual Clauses', level: 1 },
    { id: 'security-whitepaper', label: 'Security Whitepaper' },
    { id: 'audit-reporting', label: 'Audit & Reporting' },
    { id: 'audit-logs', label: 'Audit Log Retention', level: 1 },
    { id: 'audit-export', label: 'Audit Log Export', level: 1 },
    { id: 'penetration-testing', label: 'Penetration Testing', level: 1 },
    { id: 'sub-processors', label: 'Sub-processors' },
    { id: 'data-residency', label: 'Data Residency' },
  ];

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <Section id="overview" title="Compliance & Trust">
          <P>
            LogPulse is committed to meeting the compliance and regulatory requirements of our
            customers. This page provides a transparent view of our current compliance status,
            roadmap, data processing practices, and available documentation.
          </P>
          <P>
            For a high-level overview of our security posture, see the{' '}
            <Link to="/security" className="text-primary-600 dark:text-primary-400 hover:underline">
              Security page
            </Link>. For questions about compliance or to request documentation, contact{' '}
            <a href="mailto:security@logpulse.io" className="text-primary-600 dark:text-primary-400 hover:underline">
              security@logpulse.io
            </a>.
          </P>
        </Section>

        <Section id="compliance-roadmap" title="Compliance Roadmap">
          <P>
            The table below shows the current status of our compliance certifications and programs.
            We are transparent about what we have achieved and what is in progress.
          </P>
          <Table
            headers={['Framework', 'Status', 'Expected Completion', 'Details']}
            rows={[
              ['GDPR', 'Compliant', 'Completed', 'Full compliance with EU General Data Protection Regulation. DPA available.'],
              ['SOC 2 Type II', 'In Progress', 'Q3 2026', 'Audit engagement active with independent auditor. Trust Services Criteria aligned.'],
              ['ISO 27001', 'Planned', '2027', 'Information security management system certification. Scoping phase.'],
              ['HIPAA', 'Ready', 'Available on Enterprise', 'BAA available for Enterprise plan customers handling PHI.'],
              ['NIS2', 'Monitoring', 'As required', 'Tracking EU NIS2 directive requirements applicable to our sector.'],
            ]}
          />
          <Callout type="info">
            We update this roadmap as milestones are reached. Subscribe to our changelog or contact
            us for notification when a certification is completed.
          </Callout>

          <SubSection id="gdpr" title="GDPR">
            <P>
              LogPulse is fully compliant with the EU General Data Protection Regulation. Key
              capabilities include:
            </P>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1.5 mb-3 ml-2">
              <li>Data Processing Agreement (DPA) available for all paid plans</li>
              <li>Right to erasure (Article 17) via targeted deletion API</li>
              <li>Right to data portability (Article 20) via export API</li>
              <li>Data minimization through configurable retention policies</li>
              <li>EU data residency by default (GCP Netherlands, europe-west4)</li>
              <li>Sub-processor transparency with advance notification of changes</li>
            </ul>
          </SubSection>

          <SubSection id="soc2" title="SOC 2 Type II">
            <P>
              LogPulse is currently undergoing a SOC 2 Type II audit with an independent auditor.
              The audit covers the Security, Availability, and Confidentiality trust services
              criteria. We expect the audit to complete in Q3 2026.
            </P>
            <P>
              Controls already in place include continuous monitoring, access reviews, change
              management procedures, incident response plans, and vendor risk management. A
              SOC 2 Type II report will be available under NDA upon request once the audit
              is completed.
            </P>
          </SubSection>

          <SubSection id="iso27001" title="ISO 27001">
            <P>
              ISO 27001 certification is planned for 2027. We are currently in the scoping phase,
              defining the Information Security Management System (ISMS) boundaries. Our security
              controls are already aligned with many ISO 27001 requirements through our SOC 2
              program.
            </P>
          </SubSection>

          <SubSection id="hipaa" title="HIPAA">
            <P>
              LogPulse supports HIPAA-eligible configurations on the Enterprise plan. This includes
              encryption at rest (AES-256), encryption in transit (TLS 1.3), comprehensive audit
              logging of all data access, and a signed Business Associate Agreement (BAA).
            </P>
            <P>
              HIPAA requires a minimum 6-year retention period for certain records. Enterprise plan
              customers can configure retention policies up to 2,190 days (6 years) or use archival
              exports to their own HIPAA-compliant storage.
            </P>
          </SubSection>
        </Section>

        <Section id="dpa" title="Data Processing Agreement (DPA)">
          <P>
            A Data Processing Agreement is available for all paid LogPulse plans. The DPA covers:
          </P>
          <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1.5 mb-3 ml-2">
            <li>Scope of data processing and categories of personal data</li>
            <li>Sub-processor list with advance notification of changes</li>
            <li>Data subject rights procedures</li>
            <li>Security measures and incident notification (72-hour notification window)</li>
            <li>Data return and deletion upon contract termination</li>
            <li>International transfer safeguards</li>
          </ul>
          <P>
            To request a DPA, contact <a href="mailto:legal@logpulse.io" className="text-primary-600 dark:text-primary-400 hover:underline">legal@logpulse.io</a> or
            your account manager. Signed DPAs are typically returned within 5 business days.
          </P>

          <SubSection id="sccs" title="Standard Contractual Clauses">
            <P>
              For transfers of personal data outside the European Economic Area, our DPA includes
              the EU Standard Contractual Clauses (SCCs) as adopted by the European Commission
              (June 2021 version). The SCCs apply to any sub-processor located outside the EEA.
              AI processing no longer relies on them: inference runs on Amazon Bedrock in
              eu-north-1 (Stockholm), so it stays inside the EU.
            </P>
            <P>
              LogPulse's primary infrastructure (compute, storage, databases) is hosted entirely
              within the EU (GCP Netherlands, via Railway and ClickHouse Cloud). No customer log data leaves the EU
              during normal operation.
            </P>
          </SubSection>
        </Section>

        <Section id="security-whitepaper" title="Security Whitepaper">
          <P>
            A detailed security whitepaper is available on request. The whitepaper covers:
          </P>
          <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1.5 mb-3 ml-2">
            <li>Platform architecture and security design principles</li>
            <li>Encryption standards and key management</li>
            <li>Multi-tenancy isolation model</li>
            <li>Access control and authentication</li>
            <li>Incident response procedures</li>
            <li>Vulnerability management program</li>
            <li>Business continuity and disaster recovery</li>
          </ul>
          <P>
            Request the security whitepaper by contacting{' '}
            <a href="mailto:security@logpulse.io" className="text-primary-600 dark:text-primary-400 hover:underline">
              security@logpulse.io
            </a>.
          </P>
        </Section>

        <Section id="audit-reporting" title="Audit & Reporting">
          <SubSection id="audit-logs" title="Audit Log Retention">
            <P>
              LogPulse maintains comprehensive audit logs for all administrative actions, including
              user logins, API key creation and usage, configuration changes, data exports, and
              deletion requests. Audit logs are retained for the following periods:
            </P>
            <Table
              headers={['Plan', 'Audit Log Retention']}
              rows={[
                ['Free', `${AUDIT_RETENTION_DAYS} days`],
                ['Pro', `${AUDIT_RETENTION_DAYS} days`],
                ['Business', `${AUDIT_RETENTION_DAYS} days`],
                ['Enterprise', 'Custom (up to 2,190 days)'],
              ]}
            />
          </SubSection>

          <SubSection id="audit-export" title="Audit Log Export">
            <P>
              On every plan, audit events are stored in the <code>_audit</code> index with full
              metadata (timestamp, actor, action, resource, IP address, user agent). Search them
              with <code>index=_audit</code>, or pull them through the search API with a personal
              access token.
            </P>
            <Callout type="info">
              A dedicated CSV/JSON export and real-time streaming of audit events are on the
              roadmap and not available yet.
            </Callout>
          </SubSection>

          <SubSection id="penetration-testing" title="Penetration Testing">
            <P>
              LogPulse undergoes annual third-party penetration testing conducted by an independent
              security firm. The scope covers the web application, API endpoints, infrastructure,
              and authentication systems. A summary of findings and remediation status is available
              on request under NDA.
            </P>
          </SubSection>
        </Section>

        <Section id="sub-processors" title="Sub-processors">
          <P>
            LogPulse uses a minimal set of sub-processors to deliver the platform. We select
            sub-processors based on security posture, compliance certifications, and data
            residency capabilities.
          </P>
          <Table
            headers={['Sub-processor', 'Purpose', 'Location', 'Certifications']}
            rows={[
              ['Railway (GCP)', 'Application hosting, PostgreSQL, Redis', 'EU (Netherlands)', 'SOC 2, ISO 27001 (via GCP)'],
              ['ClickHouse Cloud (GCP)', 'Log storage & analytics', 'EU (Netherlands)', 'SOC 2, ISO 27001 (via GCP)'],
              ['Amazon Web Services (Bedrock)', 'AI Investigator & query generation (Claude models)', 'EU (Stockholm, eu-north-1)', 'SOC 2 Type II, ISO 27001, HIPAA'],
              ['Microsoft Azure', 'Email alerting via Microsoft Graph API', 'EU (West Europe)', 'SOC 2, ISO 27001, HIPAA'],
              ['Cloudflare', 'CDN, DDoS protection, DNS, WAF', 'Global (EU-proxied)', 'SOC 2, ISO 27001, PCI DSS'],
              ['Mollie', 'Payment processing (iDEAL, SEPA, credit card)', 'EU (Netherlands)', 'SOC 2, PCI DSS Level 1'],
            ]}
          />
          <P>
            Changes to the sub-processor list are communicated to customers with DPAs at least
            30 days in advance, giving you time to review and object if necessary.
          </P>
        </Section>

        <Section id="data-residency" title="Data Residency">
          <P>
            By default, all LogPulse customer data (logs, metadata, backups) is stored in the
            European Union, specifically in GCP Netherlands (via Railway and ClickHouse Cloud). This applies to all
            plans.
          </P>
          <Table
            headers={['Data Type', 'Storage Location', 'Notes']}
            rows={[
              ['Log data (hot, warm)', 'EU (Netherlands)', 'ClickHouse Cloud on GCP'],
              ['Log data (cold/archive)', 'EU (Netherlands)', 'ClickHouse Cloud on GCP'],
              ['Metadata (users, orgs, config)', 'EU (Netherlands)', 'PostgreSQL on Railway (GCP)'],
              ['Backups', 'EU (Netherlands)', 'Automated backups within GCP Netherlands'],
              ['Payment data', 'Mollie (EU/Netherlands)', 'Processed by Mollie; not stored by LogPulse'],
            ]}
          />
          <P>
            US region availability is planned for Enterprise customers on request. Contact your
            account manager to discuss data residency requirements for other regions.
          </P>
        </Section>
      </div>

      <TableOfContents items={toc} />
    </div>
  );
}
